import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { darkColors, lightColors } from '../theme';
import { createCommonStyles } from '../theme/styles';
import tenantService from '../services/tenantService';

const LogoUpload = ({ 
  logoUrl, 
  onUploadSuccess, 
  onRemove, 
  onError,
  maxSizeMB = 5,
  width = 200,
  height = 120
}) => {
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Debug log za logoUrl prop
  console.log('LogoUpload received logoUrl prop:', logoUrl);
  console.log('LogoUpload logoUrl type:', typeof logoUrl);
  console.log('LogoUpload logoUrl truthy:', !!logoUrl);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    if (file) {
      validateAndSelectFile(file);
    }
  };

  const validateAndSelectFile = async (file) => {
    console.log('Validating file:', file.name, file.size, file.type);
    
    // Validacija veličine
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`Fajl je prevelik. Maksimalna veličina je ${maxSizeMB}MB.`);
      return;
    }
    
    // Validacija tipa
    if (!file.type.startsWith('image/')) {
      alert('Molimo odaberite sliku.');
      return;
    }

    console.log('File validation passed, starting upload...');
    
    // Upload fajla
    setUploading(true);
    try {
      console.log('Uploading file via tenantService...');
      
      // Pozovi tenantService za upload loga
      const result = await tenantService.uploadLogo(file);
      console.log('Upload result:', result);
      console.log('Result logoUrl:', result.logoUrl);
      console.log('Result imageUrl:', result.imageUrl);
      console.log('Full result object:', JSON.stringify(result, null, 2));
      
      // Pozovi callback sa logoUrl (proba sa različitim nazivima)
      if (onUploadSuccess) {
        const logoUrl = result.logoUrl || result.imageUrl || result.url || result.path;
        console.log('Using logoUrl:', logoUrl);
        onUploadSuccess(logoUrl);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      if (onError) {
        onError('Greška pri upload-u loga. Proverite internetsku konekciju.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSelectFile(files[0]);
    }
  };

  return (
    <Box sx={{ maxWidth: '400px', mx: 'auto' }}> {/* Ograniči širinu i centriraj */}
      <Typography variant="h6" gutterBottom sx={{ 
        ...commonStyles.textPrimary, 
        textAlign: 'center' // Centriraj naslov
      }}>
        Logo
      </Typography>
      
      <Box 
        sx={{ 
          border: `2px dashed ${dragOver ? customColors.primary.main : customColors.border.light}`, 
          borderRadius: 2, 
          p: 2, // Smanji padding sa 3 na 2
          textAlign: 'center',
          background: dragOver 
            ? `${customColors.primary.main}10` 
            : customColors.background.glassWeak,
          transition: 'all 0.2s ease',
          cursor: logoUrl ? 'default' : 'pointer',
          maxWidth: '350px', // Ograniči širinu boxa
          mx: 'auto' // Centriraj box
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!logoUrl && !uploading) {
            document.getElementById('logo-file-input').click();
          }
        }}
      >
        {logoUrl ? (
          <Box>
            <Box sx={{ mb: 2 }}>
              <img 
                src={logoUrl} 
                alt="Logo preview" 
                style={{ 
                  maxWidth: `${Math.min(width * 1.4, 280)}px`, // Povećano sa 0.8 na 1.4, max 280px
                  maxHeight: `${Math.min(height * 1.4, 168)}px`, // Povećano sa 0.8 na 1.4, max 168px
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }} 
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                sx={commonStyles.outlinedButton}
                size="small"
              >
                Promeni logo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                size="small"
              >
                Ukloni
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <ImageIcon sx={{ 
              fontSize: 36, // Smanji sa 48 na 36
              color: customColors.text.secondary, 
              mb: 1,
              opacity: dragOver ? 0.7 : 1 
            }} />
            <Typography variant="body2" sx={{ ...commonStyles.textSecondary, mb: 1.5, fontSize: '0.9rem' }}> {/* Smanji margin i font */}
              {dragOver ? 'Otpustite da dodate logo' : 'Dodajte logo klijenta'}
            </Typography>
            <Typography variant="caption" sx={{ ...commonStyles.textSecondary, mb: 1.5, display: 'block', fontSize: '0.75rem' }}> {/* Smanji margin i font */}
              Prevucite sliku ovde ili kliknite da odaberete
            </Typography>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              sx={commonStyles.gradientButton}
              onClick={(e) => e.stopPropagation()}
            >
              {uploading ? 'Upload u toku...' : 'Odaberite logo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            <Typography variant="caption" sx={{ 
              ...commonStyles.textSecondary, 
              mt: 1, 
              display: 'block',
              fontSize: '0.7rem'
            }}>
              Maksimalna veličina: {maxSizeMB}MB • PNG, JPG, GIF
            </Typography>
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        id="logo-file-input"
        type="file"
        hidden
        accept="image/*"
        onChange={handleFileSelect}
      />
    </Box>
  );
};

export default LogoUpload;